import { KeenIcon } from '@/components';
import { useEffect, useState, forwardRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { getGlobalSelectClass } from '@/utils';
import { LazyFormikSelect } from '../../components/form/LazyFormikSelect';
import { ConfirmationModal } from '@/components/modal/ConfirmationModal';
import customFieldService from '@/services/setting/customFieldService';
import { createOptionLabel, splitOptionInput } from './optionInput';

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Name is required'),
  type: Yup.string().required('Type is required'),
  field_type: Yup.string().required('Field type is required'),
  is_required: Yup.boolean(),
  is_show: Yup.boolean(),

  options: Yup.mixed().test(
    'options-required-if-dropdown-or-multicheckbox',
    'At least one option is required',
    function (value) {
      const { field_type, is_multi_select } = this.parent;

      const needsOptions = field_type === 'dropdown' || field_type === 'radio';
      if (needsOptions) {
        return Array.isArray(value) && value.length > 0;
      }

      return true;
    }
  ),
});

// Heading for the options editor, keyed by the field types that carry options.
const OPTION_LABELS = { dropdown: 'Dropdown', checkbox: 'Checkbox', radio: 'Radio' };

const recordsWithValues = (count) =>
  count === 1 ? '1 record already has a value' : `${count} records already have a value`;

// Modal component
const CustomFieldModal = forwardRef((props, formikRef) => {
  const {
    open,
    onClose,
    mode = 'create',
    data,
    onSubmit,
    createFieldLoading,
    updateFieldLoading = false,
    moduleType,
    hideUserField = false,
    recordTypeId = null,
    sectionId = null,
  } = props;

  const isEdit = mode === 'edit';
  const editedFieldId = isEdit ? data?.id : null;

  // How many records already hold a value, so a type change can warn first.
  // The settings page loads it with the field; other callers pass a list row.
  const [usageCount, setUsageCount] = useState(0);
  const [pendingSubmit, setPendingSubmit] = useState(null);

  useEffect(() => {
    if (!open || !editedFieldId) {
      setUsageCount(0);
      return undefined;
    }
    if (typeof data?.usage_count === 'number') {
      setUsageCount(data.usage_count);
      return undefined;
    }

    let cancelled = false;
    customFieldService.getSingleCustomFields(editedFieldId)
      .then(({ data: field }) => {
        if (!cancelled) setUsageCount(field?.usage_count || 0);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [open, editedFieldId, data?.usage_count]);

  if (!open) return null;
  // Record-type fields expose a Half/Full width control for the layout editor.
  const showWidth = moduleType === 'matter' || !!recordTypeId;

  const parsedOptions = (() => {
    if (Array.isArray(data?.options)) {
      return data.options;
    }
    if (typeof data?.options === 'string') {
      try {
        return JSON.parse(data.options);
      } catch (e) {
        console.error('Failed to parse options', e);
        return [];
      }
    }
    return [];
  })();

  const initialValues = {
    title: data?.title || '',
    type: data?.type || moduleType || '',
    placeholder: data?.placeholder || '',
    field_type: data?.field_type || '',
    options: parsedOptions.map((opt) => ({ label: opt, value: opt })),
    is_required: data?.is_required || false,
    is_show: false, // data?.is_show ||
    is_multi_select: data?.is_multi_select || false,
    width: data?.width || 'half',
    date_constraint: data?.date_constraint || '',
  };

  return (
    <div className="modal-wrapper modal-animate-fade">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        innerRef={formikRef}
        onSubmit={(values, formikHelpers) => {
          const hasOptions = Boolean(OPTION_LABELS[values.field_type]);
          const payload = {
            ...values,
            // Options only belong to option types; drop them when the type changes away.
            options: hasOptions ? values.options?.map((opt) => opt.value) : [],
            // Only dropdowns and checkbox lists hold several answers. The server
            // rejects multi-select on any other type, so a type change must clear it.
            is_multi_select: values.field_type === 'checkbox'
              ? values.options?.length > 0
              : values.field_type === 'dropdown' && values.is_multi_select,
            // Date restriction only applies to date fields; clear it otherwise so a
            // stale value can't ride along when the field type changes.
            date_constraint: values.field_type === 'date' ? (values.date_constraint || null) : null,
          };
          // Attach record-type placement only for record-type fields; leaving these
          // absent on a generic edit keeps the field's existing placement untouched.
          if (recordTypeId) {
            payload.record_type_id = recordTypeId;
            if (sectionId) payload.record_type_section_id = sectionId;
          }
          if (!showWidth) delete payload.width;

          // Saved values are not converted, so ask before changing the type of
          // a field that records already use.
          if (isEdit && usageCount > 0 && values.field_type !== data?.field_type) {
            setPendingSubmit({ payload, formikHelpers });
            formikHelpers.setSubmitting(false);
            return;
          }
          onSubmit(payload, formikHelpers);
        }}
      >
        {({ values, handleChange, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit} className="modal-animate-scale modal-body w-4/5 max-w-md">
            <h3 className="card-title capitalize mb-5">{mode} Custom Field</h3>
            <button
              type="button"
              onClick={onClose}
              className="modal-close-btn"
            >
              <KeenIcon icon="cross" />
            </button>

            <div>
              <label className="form-label mb-1">Name <span className='pl-px text-red-600'>*</span></label>
              <Field name="title" className="input" />
              <ErrorMessage name="title" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div className='my-4'>
              <label className="form-label mb-1">Type <span className='pl-px text-red-600'>*</span></label>
              <LazyFormikSelect
                name="type"
                options={[
                  { label: 'Contact', value: 'contact' },
                  { label: 'Matter', value: 'matter' },
                  ...(!hideUserField ? [{ label: 'User', value: 'user' }] : []),
                ]}
                disabled={isEdit || moduleType}
              />
            </div>

            <div>
              <label className="form-label mb-1">Placeholder (Help Text)</label>
              <Field name="placeholder" className="input" />
              <ErrorMessage name="placeholder" component="div" className="text-red-500 text-xs mt-1" />
            </div>

            <div className='my-4'>
              <label className="form-label mb-1">Field Type <span className='pl-px text-red-600'>*</span></label>
              <LazyFormikSelect
                name="field_type"
                options={[
                  { label: 'Date', value: 'date' },
                  { label: 'Drop Down List', value: 'dropdown' },
                  { label: 'Text', value: 'text' },
                  { label: 'Text Area', value: 'textarea' },
                  { label: 'Number', value: 'number' },
                  { label: 'Currency', value: 'currency' },
                  { label: 'Phone', value: 'phone' },
                  { label: 'Checkbox', value: 'checkbox' },
                  { label: 'Radio Buttons', value: 'radio' },
                ]}
                value={values.field_type}
              />
              {isEdit && usageCount > 0 && values.field_type !== data?.field_type && (
                <p className="mt-1 text-xs text-warning">
                  {recordsWithValues(usageCount)} in this field. Changing the type
                  does not convert those values, so some may show blank until they are re-entered.
                </p>
              )}
            </div>

            {values.field_type === 'date' && (
              <div className="my-4">
                <label className="mb-1 form-label">Date Restriction</label>
                <LazyFormikSelect
                  name="date_constraint"
                  options={[
                    { label: 'No restriction', value: '' },
                    { label: 'No future dates', value: 'no_future' },
                    { label: 'No past dates', value: 'no_past' },
                  ]}
                  value={values.date_constraint}
                />
              </div>
            )}

            {showWidth && (
              <div className="my-4">
                <label className="mb-1 form-label">Field Width</label>
                <LazyFormikSelect
                  name="width"
                  options={[
                    { label: 'Half', value: 'half' },
                    { label: 'Full', value: 'full' },
                  ]}
                  value={values.width}
                />
              </div>
            )}

            {['dropdown', 'checkbox', 'radio'].includes(values.field_type) && (
              <div>
                <label className="form-label mb-1">{OPTION_LABELS[values.field_type] || 'Dropdown'} Options {values.field_type === 'checkbox' && '(Add labels for each checkbox)'}</label>
                <CreatableSelect
                  isMulti
                  value={values.options}
                  onChange={(selected) => setFieldValue('options', selected)}
                  onCreateOption={(input) => {
                    const added = splitOptionInput(input, values.options).map((label) => ({ label, value: label }));
                    setFieldValue('options', [...(values.options || []), ...added]);
                  }}
                  isValidNewOption={(input) => splitOptionInput(input, values.options).length > 0}
                  placeholder="Type and press Enter"
                  options={[]}
                  isClearable
                  isSearchable
                  classNames={getGlobalSelectClass()}
                  className="input react-select-container p-0"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => 'Start typing to create options'}
                  formatCreateLabel={(inputValue) => createOptionLabel(inputValue, values.options)}
                  components={{
                    DropdownIndicator: null,
                  }}
                />
                <div className="text-xs text-gray-500 mt-1">Separate options with commas to add several at once.</div>
                <ErrorMessage name="options" component="div" className="text-red-500 text-xs mt-1" />
              </div>
            )}

            <div className="flex items-center justify-between gap-2 my-4">
              {['dropdown'].includes(values.field_type) && (
                <Field name="is_multi_select">
                  {({ field, form }) => (
                    <label className="switch w-fit !gap-1.5">
                      <span className="switch-label !text-xs">Allow Multiple Selection</span>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={() => form.setFieldValue("is_multi_select", !field.value)}
                      />
                    </label>
                  )}
                </Field>
              )}
              {!hideUserField &&
                <Field name="is_required">
                  {({ field, form }) => (
                    <label className="switch w-fit !gap-1.5">
                      <span className="switch-label !text-xs">Required ?</span>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={() => form.setFieldValue("is_required", !field.value)}
                      />
                    </label>
                  )}
                </Field>
              }
            </div>

            <button type="submit" disabled={createFieldLoading || updateFieldLoading} className="btn btn-primary justify-center w-full mt-2">
              {mode === 'edit' ?
                updateFieldLoading ? 'Please Wait...' : 'Update Field'
                : createFieldLoading ? 'Please wait...' : 'Create Field'}
            </button>
          </Form>
        )}
      </Formik>

      <ConfirmationModal
        show={Boolean(pendingSubmit)}
        title="Change the field type?"
        message={`${recordsWithValues(usageCount)} in this field. Their saved values will not be converted, so some may show blank until they are re-entered.`}
        btnText="Change type"
        customBtnClass="btn-warning"
        onClose={() => setPendingSubmit(null)}
        onConfirm={() => {
          const { payload, formikHelpers } = pendingSubmit;
          setPendingSubmit(null);
          onSubmit(payload, formikHelpers);
        }}
      />
    </div>
  );
});

export { CustomFieldModal };
